<script setup lang="ts">
import { useDefaults } from 'vuetify'
import type { IdNumber } from '../../../types/_general'
import Personnummer from 'personnummer'
import Organisationsnummer from 'organisationsnummer'
import { computed, ref, watch, watchEffect, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { localeKey } from '../../../utils/localeKey'
import { makeVTextFieldProps, VTextField, type VTextFieldSlots } from 'vuetify/lib/components/VTextField/VTextField.mjs'
import { formatList } from '../../../utils/format'

const _props = defineProps({
  mode: {
    type: String as PropType<'person' | 'company' | 'foreign' | null>,
    required: false,
    default: () => null,
    validator: (value: string) => ['person', 'company', 'foreign'].includes(value),
  },
  allowedTypes: {
    type: Array as PropType<readonly IdNumber[]>,
    required: false,
    validator: (value: string[]) =>
      value.every((type) =>
        [
          'swedish_person',
          'swedish_coordination_person',
          'swedish_company',
          'other_person',
          'other_company',
          'foreign',
        ].includes(type)
      ),
    default: () => [],
  },
  ...makeVTextFieldProps(),
})
const props = useDefaults(_props)

const typesPerMode: Record<NonNullable<typeof _props.mode>, IdNumber[]> = {
  person: ['swedish_person', 'swedish_coordination_person', 'other_person'],
  company: ['swedish_company', 'other_company'],
  foreign: ['foreign'],
}

const slots = defineSlots<Omit<VTextFieldSlots, 'default' | 'append-inner'>>()

const i18n = useI18n()

const model = defineModel<string | null>()
const isFocused = ref(props.focused)
const emit = defineEmits<{
  'type': [idType: IdNumber | undefined]
  'update:focused': [focus: boolean]
}>()

const rawValue = ref(model.value)
const formattedValue = ref(model.value)
const idType = ref<IdNumber>()

const chipColor = computed(() => {
  switch (idType.value) {
    case 'swedish_person':
      return 'green'
    case 'swedish_coordination_person':
      return 'orange'
    case 'swedish_company':
      return 'blue'
    default:
      // All "other" types show up as the default grey invalid color
      return undefined
  }
})

const extendedRules = computed(() => {
  const allowedTypesForMode =
    props.mode != null
      ? typesPerMode[props.mode].filter((type) => props.allowedTypes.includes(type))
      : props.allowedTypes
  const translatedTypesForMode = allowedTypesForMode.map((type) => localeKey(type, ['id_number', 'common'], true, i18n))

  return [
    ...props.rules,
    ...(props.allowedTypes.length > 0
      ? [
          () =>
            (idType.value != null && props.allowedTypes.includes(idType.value)) ||
            i18n.t('common.id_number_must_be_type_x', { x: formatList(translatedTypesForMode, 'disjunction') }),
        ]
      : []),
  ]
})

watchEffect(() => {
  emit('type', idType.value)
})

watch(
  () => props.focused,
  (focus) => {
    isFocused.value = focus
  }
)
watch(isFocused, (focus) => {
  emit('update:focused', focus)
  // Blur resets the raw text to the canonical model, never to the formatted display value,
  // so every later focus shows the unmasked number (the model watcher below does the same).
  if (!focus) {
    rawValue.value = model.value ?? ''
  }
})

watch(
  rawValue,
  (val) => {
    if (val == null || val === '') {
      formattedValue.value = ''
      model.value = null
      idType.value = undefined
    } else if (props.mode == null) {
      if (Personnummer.valid(val)) {
        const p = Personnummer.parse(val)
        formattedValue.value = p.format()
        model.value = p.format(true)
        idType.value = p.isCoordinationNumber() ? 'swedish_coordination_person' : 'swedish_person'
      } else if (Organisationsnummer.valid(val)) {
        const o = Organisationsnummer.parse(val)
        formattedValue.value = o.format()
        model.value = o.format(false)
        idType.value = 'swedish_company'
      } else {
        formattedValue.value = val
        model.value = val
        idType.value = 'other_person'
      }
    } else if (props.mode === 'person') {
      if (Personnummer.valid(val)) {
        const p = Personnummer.parse(val)
        formattedValue.value = p.format()
        model.value = p.format(true)
        idType.value = p.isCoordinationNumber() ? 'swedish_coordination_person' : 'swedish_person'
      } else {
        formattedValue.value = val
        model.value = val
        idType.value = 'other_person'
      }
    } else if (props.mode === 'company') {
      if (Organisationsnummer.valid(val)) {
        const o = Organisationsnummer.parse(val)
        formattedValue.value = o.format()
        model.value = o.format(false)
        idType.value = 'swedish_company'
      } else {
        formattedValue.value = val
        model.value = val
        idType.value = 'other_company'
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (props.mode === 'foreign') {
      formattedValue.value = val
      model.value = val
      idType.value = 'foreign'
    }
  },
  { immediate: true }
)

watch(model, (val) => {
  if (!isFocused.value) {
    rawValue.value = val ?? ''
  }
})
</script>

<template>
  <v-text-field
    v-bind="VTextField.filterProps(props)"
    v-model:focused="isFocused"
    :model-value="isFocused ? rawValue : formattedValue"
    :validation-value="model"
    :rules="extendedRules"
    @update:model-value="rawValue = $event"
  >
    <template #append-inner>
      <v-chip
        v-if="idType"
        :color="chipColor"
      >
        <span class="d-sr-only">{{ i18n.t('common.id_number_type') }}: </span
        >{{ localeKey(idType, ['id_number', 'common'], true, i18n) }}
      </v-chip>
    </template>
    <template
      v-if="slots.append"
      #append
    >
      <slot name="append" />
    </template>
  </v-text-field>
</template>
